import { useEffect, useRef } from 'react'
import { nearestViewMiles, cameraViewKey, cameraStep } from '../../utils/mapCamera' // s101 map review
import { loadGoogleMaps } from '../../utils/googleMaps'
import orellaO from '../../assets/logos/orella_o_white.png'
import { clinicianName } from '../../utils/clinicianName.js'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// ★ s101 Phase 4 (TestFlight b65: "use real logo pin"): the Orella pin carries the REAL O mark
//   (assets/logos/orella_o_white.png) on the navy teardrop, composed once on a canvas into a data
//   URL. Until the image has loaded (or if it never does) the drawn O below is used.
const logoPinCache = {}
let logoImg = null
let logoPromise = null
function loadLogo() {
  if (logoPromise) return logoPromise
  logoPromise = new Promise((resolve) => {
    if (typeof Image === 'undefined') { resolve(null); return }
    const img = new Image()
    img.onload = () => { logoImg = img; resolve(img) }
    img.onerror = () => resolve(null)
    img.src = orellaO
  })
  return logoPromise
}
function logoPinUrl(sel) {
  const key = sel ? 'sel' : 'base'
  if (logoPinCache[key] !== undefined) return logoPinCache[key]
  if (!logoImg || typeof document === 'undefined') return null
  try {
    const scale = 3
    const w = (sel ? 42 : 36) * scale
    const h = (sel ? 56 : 48) * scale
    const c = document.createElement('canvas')
    c.width = w; c.height = h
    const g = c.getContext('2d')
    g.scale(w / 38, h / 50)
    g.fillStyle = sel ? '#46A8F9' : '#15354F'
    g.strokeStyle = '#ffffff'; g.lineWidth = 2
    const path = new window.Path2D('M19 1C9 1 1 9 1 19c0 12.5 18 28 18 28s18-15.5 18-28C37 9 29 1 19 1z')
    g.fill(path); g.stroke(path)
    const d = 20
    g.drawImage(logoImg, 19 - d / 2, 18.5 - d / 2, d, d)
    logoPinCache[key] = c.toDataURL('image/png')
  } catch (e) {
    logoPinCache[key] = null
  }
  return logoPinCache[key]
}
const milesApart = (a, b) => {
  const R = 3958.8, toR = (x) => (x * Math.PI) / 180
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * DoctorsMap (v230.11.7)
 *
 * Renders MD *business* locations as teardrop pins on a Google map (navy default,
 * blue + larger when selected). The "you are here" marker is a solid blue locator
 * dot when the patient chose a precise location (device GPS / searched address) and
 * a faint area circle for the coarse zip-centroid default — precise patient location
 * is never sent to Google. Markers without coords are simply not shown. Defensive:
 * if the SDK fails to load, renders nothing and the parent keeps the list available.
 * ★ s99 D476 (Dr. Rami 2026-10-10): `orellaOf(doctor)` marks Orella doctors with the O pin (navy,
 *   larger, drawn on top) and draws everyone else as a small light-blue pin. Without it every pin
 *   is today's navy teardrop (the Doctors screen is unchanged). `orellaOf={() => true}` = an
 *   all-Orella list (the Orella doctor search).
 */
export default function DoctorsMap({ doctors, center, centerPrecise, selectedId, onSelect, mapsKey, orellaOf, fitRadiusMiles, fitExact = false, onViewportChange, youPoint, youPrecise, recenterKey, loading = false, loadingLabel = '', loadingText = '' }) {
  // ★ s101 Phase 4: "Search this area". After the PATIENT moves the map (a drag, or a zoom the
  //   camera code did not make), each settled view is reported as { lat, lng, radiusMiles } — the
  //   map's centre and the distance to its corner — so the screen can offer to search there.
  //   The camera's own moves (fitView) never report.
  const userMovedRef = useRef(false)
  const cameraMovingRef = useRef(false)
  const onViewportRef = useRef(onViewportChange)
  onViewportRef.current = onViewportChange
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const divRef = useRef(null)
  const mapRef = useRef(null)
  const markersRef = useRef(new Map()) // id -> { marker, key } (s101 map review: diffed, never all re-created)
  const youRef = useRef(null)
  // ★ s101 map review (Dr. Rami, Build 67: "every time I zoom in it auto zooms out"): the camera fits
  //   ONCE PER SEARCH. viewKey = the patient's location + the searched area + the distance; a new
  //   doctor array (every render of the screen builds one), a pin tap or a list refresh never moves
  //   it. After the patient pans or zooms, nothing moves the camera until the search itself changes.
  const camRef = useRef({ key: null, fittedWithPins: false, userTookOver: false, lastReported: null })
  const mapsRef = useRef(null)

  // Teardrop locator pin (style A): solid fill + white center dot, tail anchored to point.
  function pinIcon(maps, sel) {
    const color = sel ? '#46A8F9' : '#15354F'
    const w = sel ? 40 : 33
    const h = sel ? 53 : 44
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 38 50">` +
      `<defs><filter id="sh" x="-40%" y="-15%" width="180%" height="155%">` +
      `<feDropShadow dx="0" dy="1.5" stdDeviation="1.3" flood-color="#0c2438" flood-opacity="0.35"/></filter></defs>` +
      `<g filter="url(#sh)">` +
      `<path d="M19 1C9 1 1 9 1 19c0 12.5 18 28 18 28s18-15.5 18-28C37 9 29 1 19 1z" fill="${color}"/>` +
      `<circle cx="19" cy="18.5" r="6.6" fill="#ffffff"/></g></svg>`
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new maps.Size(w, h),
      anchor: new maps.Point(w / 2, h * 0.94),
    }
  }

  // s99 D476: the O pin (an Orella doctor) and the small pin (a directory doctor).
  function orellaPinIcon(maps, sel) {
    const logo = logoPinUrl(sel)
    if (logo) {
      const lw = sel ? 42 : 36
      const lh = sel ? 56 : 48
      return { url: logo, scaledSize: new maps.Size(lw, lh), anchor: new maps.Point(lw / 2, lh * 0.94) }
    }
    const w = sel ? 42 : 36
    const h = sel ? 56 : 48
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 38 50">` +
      `<defs><filter id="sh" x="-40%" y="-15%" width="180%" height="155%">` +
      `<feDropShadow dx="0" dy="1.5" stdDeviation="1.3" flood-color="#0c2438" flood-opacity="0.4"/></filter></defs>` +
      `<g filter="url(#sh)">` +
      `<path d="M19 1C9 1 1 9 1 19c0 12.5 18 28 18 28s18-15.5 18-28C37 9 29 1 19 1z" fill="${sel ? '#46A8F9' : '#15354F'}" stroke="#ffffff" stroke-width="2"/>` +
      `<circle cx="19" cy="18.5" r="7.2" fill="none" stroke="#ffffff" stroke-width="3"/>` +
      `<path d="M23.6 12.6l3.4-2.6" stroke="#ffffff" stroke-width="3" stroke-linecap="round"/></g></svg>`
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new maps.Size(w, h),
      anchor: new maps.Point(w / 2, h * 0.94),
    }
  }
  // s123 (board F): one pin for several clinicians at one address — a round badge with the count.
  function countPinIcon(maps, n, sel) {
    const s = sel ? 38 : 30
    const label = n > 99 ? '99+' : String(n)
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 30 30">` +
      `<circle cx="15" cy="15" r="13" fill="${sel ? '#15354F' : '#46A8F9'}" stroke="#ffffff" stroke-width="2.5"/>` +
      `<text x="15" y="19.2" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="${label.length > 2 ? 9 : 11.5}" font-weight="800" fill="#ffffff">${label}</text></svg>`
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new maps.Size(s, s),
      anchor: new maps.Point(s / 2, s / 2),
    }
  }

  function smallPinIcon(maps, sel) {
    const w = sel ? 30 : 20
    const h = sel ? 40 : 27
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 38 50">` +
      `<path d="M19 1C9 1 1 9 1 19c0 12.5 18 28 18 28s18-15.5 18-28C37 9 29 1 19 1z" fill="${sel ? '#46A8F9' : '#7FB8E6'}" stroke="#ffffff" stroke-width="3"/></svg>`
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new maps.Size(w, h),
      anchor: new maps.Point(w / 2, h * 0.94),
    }
  }

  // "You are here": prominent blue dot + accuracy halo (precise) vs faint area circle (coarse).
  function youIcon(maps, precise) {
    let svg, size
    if (precise) {
      size = 46
      svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">` +
        `<circle cx="23" cy="23" r="21" fill="#46A8F9" fill-opacity="0.16"/>` +
        `<circle cx="23" cy="23" r="9" fill="#46A8F9" stroke="#ffffff" stroke-width="3"/></svg>`
    } else {
      size = 40
      svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">` +
        `<circle cx="20" cy="20" r="18" fill="#46A8F9" fill-opacity="0.14"/>` +
        `<circle cx="20" cy="20" r="6" fill="#46A8F9" fill-opacity="0.55" stroke="#ffffff" stroke-width="2"/></svg>`
    }
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new maps.Size(size, size),
      anchor: new maps.Point(size / 2, size / 2),
    }
  }

  // s110 (mockup "Orella Directory Office Addresses", screen 2): an office whose street we do not know is drawn
  //   as an AREA, never a street pin. ★ s111 (TestFlight b75, Dr. Rami: "Amber circle too distracting, just use amber
  //   dots"): a small amber dot with a white ring; selected, a blue ring.
  function areaIcon(maps, sel) {
    const size = sel ? 22 : 16
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 22 22">` +
      `<circle cx="11" cy="11" r="${sel ? 9 : 7}" fill="#D98A1F" stroke="${sel ? '#46A8F9' : '#ffffff'}" stroke-width="${sel ? 3 : 2}"/></svg>`
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new maps.Size(size, size),
      anchor: new maps.Point(size / 2, size / 2),
    }
  }

  // Draw doctor pins + the "you are here" marker. No camera movement here so that
  // selecting a pin never pans the map.
  function renderMarkers() {
    const maps = mapsRef.current
    const map = mapRef.current
    if (!maps || !map) return
    const seen = new Set()
    ;(doctors || []).forEach((d) => {
      if (d.practice_lat == null || d.practice_lng == null) return
      const pos = { lat: Number(d.practice_lat), lng: Number(d.practice_lng) }
      if (isNaN(pos.lat) || isNaN(pos.lng)) return
      const id = String(d.id)
      seen.add(id)
      const isSel = d.id === selectedId
      const isOrella = typeof orellaOf === 'function' ? !!orellaOf(d) : null
      const key = `${isSel ? 1 : 0}|${isOrella}|${d.__area ? 1 : 0}|${pos.lat.toFixed(6)},${pos.lng.toFixed(6)}|${logoPinUrl(false) ? 1 : 0}|${d.__count || 1}`
      const have = markersRef.current.get(id)
      if (have && have.key === key) { have.doc = d; return }
      // s110: a directory office we only know the area of (utils/officeAddress) is drawn as an area.
      const icon = d.__area ? areaIcon(maps, isSel)
        : isOrella === null ? pinIcon(maps, isSel) : isOrella ? orellaPinIcon(maps, isSel)
          : d.__count > 1 ? countPinIcon(maps, d.__count, isSel) : smallPinIcon(maps, isSel)
      const zIndex = isSel ? 999 : isOrella ? 10 : d.__count > 1 ? 5 : 1
      if (have) {
        have.marker.setPosition(pos); have.marker.setIcon(icon); have.marker.setZIndex(zIndex)
        have.key = key; have.doc = d
        return
      }
      const marker = new maps.Marker({
        position: pos,
        map,
        title: d.name || clinicianName(d),
        icon,
        zIndex,
      })
      const entry = { marker, key, doc: d }
      marker.addListener('click', () => onSelectRef.current && onSelectRef.current(entry.doc))
      markersRef.current.set(id, entry)
    })
    markersRef.current.forEach((entry, id) => {
      if (!seen.has(id)) { entry.marker.setMap(null); markersRef.current.delete(id) }
    })

    // The blue dot is the patient. With "Search this area" the camera centre moves away from
    // them, so a caller may pass `youPoint` for the dot; without it the dot is the centre, as before.
    const you = youPoint !== undefined ? youPoint : center
    const youExact = youPoint !== undefined ? !!youPrecise : !!centerPrecise
    if (you && typeof you.lat === 'number' && typeof you.lng === 'number') {
      if (youRef.current) { youRef.current.setPosition(you); youRef.current.setIcon(youIcon(maps, youExact)) }
      else youRef.current = new maps.Marker({ position: you, map, icon: youIcon(maps, youExact), zIndex: 0 })
    } else if (youRef.current) { youRef.current.setMap(null); youRef.current = null }
  }

  // Camera: a precise location geolocates the patient (center on them); the coarse
  // default frames all doctor pins. Runs only on location/doctor changes — never on
  // pin selection.
  function fitView() {
    const maps = mapsRef.current
    const map = mapRef.current
    if (!maps || !map) return
    cameraMovingRef.current = true
    userMovedRef.current = false
    // s101 map review: a fit that leaves the view unchanged may never fire 'idle'; without this the
    // patient's first zoom would not count as theirs.
    setTimeout(() => { cameraMovingRef.current = false }, 1500)
    const pts = (doctors || [])
      .filter((d) => d.practice_lat != null && d.practice_lng != null)
      .map((d) => ({ lat: Number(d.practice_lat), lng: Number(d.practice_lng) }))
      .filter((p) => !isNaN(p.lat) && !isNaN(p.lng))

    // ★ s101 Phase 4 (x_23, TestFlight b65: "the map fits the whole continent"): with a search
    //   radius the camera shows THAT circle around the patient, not every pin. A list of Orella
    //   doctors spans the country, and fitting all of them zoomed out to the continent.
    if (center && Number.isFinite(fitRadiusMiles) && fitRadiusMiles > 0) {
      // ★ s101 map review ("opens very zoomed out"): frame the patient and the doctors NEAREST them
      //   (utils/mapCamera.nearestViewMiles), not the whole circle (25 mi = a 50-mile box).
      // s101 Build 67 (Dr. Rami): "the map will need to zoom to the area selected by the patient
      //   (5 mi, 10 mi, etc)" - a chosen distance is framed EXACTLY; "Any" frames the nearest doctors.
      const viewMiles = fitExact ? fitRadiusMiles : nearestViewMiles(center, doctors, fitRadiusMiles, orellaOf)
      const dLat = viewMiles / 69.0
      const dLng = viewMiles / (69.0 * Math.max(Math.cos((center.lat * Math.PI) / 180), 0.01))
      const r = new maps.LatLngBounds(
        { lat: center.lat - dLat, lng: center.lng - dLng },
        { lat: center.lat + dLat, lng: center.lng + dLng })
      map.fitBounds(r, 12)
      return
    }
    if (centerPrecise && center) {
      map.setCenter(center)
      map.setZoom(12)
      return
    }
    if (pts.length === 0) {
      if (center) { map.setCenter(center); map.setZoom(11) }
      return
    }
    const bounds = new maps.LatLngBounds()
    pts.forEach((p) => bounds.extend(p))
    if (center) bounds.extend(center)
    if (pts.length === 1 && !center) { map.setCenter(pts[0]); map.setZoom(12) }
    else map.fitBounds(bounds, 56)
  }

  // Initialize the map once the SDK is ready.
  useEffect(() => {
    let cancelled = false
    loadGoogleMaps(mapsKey)
      .then((maps) => {
        if (cancelled || !divRef.current || mapRef.current) return
        mapsRef.current = maps
        mapRef.current = new maps.Map(divRef.current, {
          center: center || { lat: 39.8, lng: -98.6 },
          zoom: 10,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: 'greedy',
        })
        // s101 Phase 4: tell the patient's moves from the camera's.
        mapRef.current.addListener('dragstart', () => { userMovedRef.current = true })
        mapRef.current.addListener('dragstart', () => { camRef.current = { ...camRef.current, userTookOver: true } })
        mapRef.current.addListener('zoom_changed', () => { if (!cameraMovingRef.current) { userMovedRef.current = true; camRef.current = { ...camRef.current, userTookOver: true } } })
        mapRef.current.addListener('idle', () => {
          cameraMovingRef.current = false
          if (!userMovedRef.current || typeof onViewportRef.current !== 'function') return
          userMovedRef.current = false
          const c = mapRef.current.getCenter()
          const b = mapRef.current.getBounds()
          if (!c || !b) return
          const ctr = { lat: c.lat(), lng: c.lng() }
          const ne = b.getNorthEast()
          const radiusMiles = Math.max(1, Math.min(100, Math.round(milesApart(ctr, { lat: ne.lat(), lng: ne.lng() }))))
          camRef.current = { ...camRef.current, lastReported: ctr }
          onViewportRef.current({ lat: ctr.lat, lng: ctr.lng, radiusMiles })
        })
        renderMarkers()
        maybeFit()
        loadLogo().then((img) => { if (img && mapRef.current) renderMarkers() })
      })
      .catch((e) => console.warn('[DoctorsMap] SDK load failed:', e?.message || e))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsKey])

  // Redraw markers on data / selection / location changes.
  useEffect(() => {
    if (mapRef.current) renderMarkers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctors, selectedId, center, centerPrecise, youPoint, youPrecise])

  // ★ s101 map review: the camera moves only when the SEARCH changes (viewKey) - or once, when the
  //   first pins of that search arrive and the patient has not touched the map yet.
  function maybeFit() {
    if (!mapRef.current) return
    const viewKey = cameraViewKey({ center, centerPrecise, fitRadiusMiles, doctors }) + (fitExact ? '|x' : '')
    const hasPins = (doctors || []).some((d) => d.practice_lat != null && d.practice_lng != null)
    const step = cameraStep(camRef.current, { viewKey, hasPins, center })
    camRef.current = step.state
    if (step.action === 'fit') fitView()
  }
  // PROD-1184: "My location" (a new `recenterKey`) frames the search again even when its centre did not
  //   change (the patient only panned). Declared before the effect below, so a tap that also changes
  //   the centre fits once.
  useEffect(() => {
    if (!recenterKey || !mapRef.current) return
    camRef.current = { ...camRef.current, key: null, lastReported: null, userTookOver: false }
    maybeFit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenterKey])
  useEffect(() => {
    maybeFit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctors, center, centerPrecise, fitRadiusMiles])

  // ★ s114 B2 (Dr. Rami 2026-10-22 build-80 bugs: "the map ... does not show a loading while outside orella doctors
  //   are loading"; his pick: spinner only, no text). `loading` = the directory search for this map is in flight.
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} aria-busy={loading ? 'true' : undefined}>
      <div ref={divRef} style={{ width: '100%', height: '100%' }} />
      {loading && (
        // ★ s123 (board B, his approval 2026-10-29): with `loadingText` the pill is the navy "Finding clinicians in this
        //   area…" one (white O); without it, the s114 spinner-only pill (the share step).
        loadingText ? (
          <div data-map-loading role="status" aria-label={loadingText}
            style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', borderRadius: 999, padding: '6px 14px 6px 7px', whiteSpace: 'nowrap',
              background: '#15354F', color: '#FFFFFF', fontSize: 13, fontWeight: 600, boxShadow: '0 4px 14px rgba(21,53,79,0.3)', display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'none', zIndex: 5 }}>
            <OrellaSpinner size={24} tone="dark" />
            {loadingText}
          </div>
        ) : (
        <div data-map-loading role="status" aria-label={loadingLabel || undefined}
          style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', width: 34, height: 34, borderRadius: 17,
            background: '#FFFFFF', boxShadow: '0 2px 8px rgba(21,53,79,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <OrellaSpinner size={22} />
        </div>
        )
      )}
    </div>
  )
}
